import { useState, useCallback } from 'react';
import { Box, Button, Chip } from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import { EquipmentPickerDialog } from '@components/EquipmentPicker';
import type { EquipmentSummary } from '@api/bff/equipment/useEquipment';
import type { EquipmentRef } from '../../types';

type Props = {
  value: EquipmentRef | null;
  onChange: (equipment: EquipmentRef | null) => void;
  disabled?: boolean;
};

export const EquipmentPicker = ({
  value,
  onChange,
  disabled = false
}: Props) => {
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const handleSelect = useCallback(
    (equipment: EquipmentSummary) =>
      onChange({
        companyName: equipment.companyName,
        id: equipment.id,
        globalId: equipment.globalId
      }),
    [onChange]
  );

  const handleClear = useCallback(() => onChange(null), [onChange]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <ScienceIcon fontSize="small" color="primary" />
      {value ? (
        <Chip
          label={`${value.companyName} — ${value.id}`}
          size="small"
          variant="outlined"
          color="primary"
          onClick={disabled ? undefined : handleOpen}
          onDelete={disabled ? undefined : handleClear}
        />
      ) : (
        <>
          <Chip
            label="No equipment (dry run)"
            size="small"
            variant="outlined"
            color="warning"
            onClick={disabled ? undefined : handleOpen}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={handleOpen}
            disabled={disabled}
          >
            Select Equipment
          </Button>
        </>
      )}
      <EquipmentPickerDialog
        open={open}
        defaultCompanyName={value?.companyName ?? null}
        selectedGlobalId={value?.globalId ?? null}
        onClose={handleClose}
        onSelect={handleSelect}
      />
    </Box>
  );
};
